// API Service for ProviderDrop Frontend
// Handles all communication with the Go backend

const API_BASE = '/api';

/**
 * Handles API response and error handling
 * @param {Response} response
 * @returns {Promise<any>}
 */
async function handleResponse(response) {
  const data = await response.json().catch(() => null);
  
  if (!response.ok) {
    const error = new Error(data?.error || `Request failed with status ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  
  return data;
}

/**
 * Make a GET request
 * @param {string} endpoint
 * @param {Object} options
 * @returns {Promise<any>}
 */
async function get(endpoint, options = {}) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
    ...options,
  });
  return handleResponse(response);
}

/**
 * Make a POST request
 * @param {string} endpoint
 * @param {Object} body
 * @param {Object} options
 * @returns {Promise<any>}
 */
async function post(endpoint, body, options = {}) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
    body: JSON.stringify(body),
    ...options,
  });
  return handleResponse(response);
}

/**
 * Upload files with multipart/form-data
 * @param {string} endpoint
 * @param {FormData} formData
 * @param {string} sessionToken
 * @returns {Promise<any>}
 */
async function uploadFiles(endpoint, formData, sessionToken) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      'X-Session-Token': sessionToken,
    },
    credentials: 'include',
    body: formData,
  });
  return handleResponse(response);
}

function toUploadIncidentPayload({ step, operation, error, sessionToken, email, metadata = {} }) {
  const statusCode = typeof error?.status === 'number' ? error.status : 0;
  const message = error?.message || 'Unknown upload error';

  return {
    sessionToken,
    email,
    step,
    operation,
    message,
    statusCode,
    metadata: {
      ...metadata,
      errorName: error?.name,
    },
  };
}

export function isReportableUploadError(error) {
  if (!error) {
    return false;
  }

  if (typeof error.status === 'number') {
    return error.status >= 500;
  }

  return true;
}

export async function reportUploadClientError(details) {
  const payload = toUploadIncidentPayload(details);

  if (!payload.message) {
    return;
  }

  try {
    await fetch(`${API_BASE}/upload/client-error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(payload.sessionToken
          ? { 'X-Session-Token': payload.sessionToken }
          : {}),
      },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify(payload),
    });
  } catch {
    // Best-effort telemetry only.
  }
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Get public configuration for landing page
 * @returns {Promise<{organization: Object, features: Object}>}
 */
export async function getPublicConfig() {
  return get('/public/config');
}

// =============================================================================
// UPLOAD FLOW API
// =============================================================================

/**
 * Start upload session and send verification email
 * @param {string} email
 * @returns {Promise<{message: string, sessionToken: string, expiresIn: number, emailDisabled?: boolean}>}
 */
export async function startUpload(email) {
  return post('/upload/start', { email });
}

/**
 * Verify email with OTP code
 * @param {string} sessionToken
 * @param {string} code
 * @returns {Promise<{verified: boolean, message: string, rememberToken?: string, rememberExpiresIn?: number}>}
 */
export async function verifyEmail(sessionToken, code) {
  return post('/upload/verify-email', { sessionToken, code });
}

/**
 * Check if a remember token is valid for an email
 * @param {string} email
 * @param {string} rememberToken
 * @returns {Promise<{valid: boolean, sessionToken?: string, message: string}>}
 */
export async function checkRememberToken(email, rememberToken) {
  return post('/upload/check-remember', { email, rememberToken });
}

/**
 * Get remember verification settings
 * @returns {Promise<{enabled: boolean, durationHours: number}>}
 */
export async function getRememberSettings() {
  return get('/upload/remember-settings');
}

/**
 * Resend verification code
 * @param {string} sessionToken
 * @returns {Promise<{message: string, expiresIn: number}>}
 */
export async function resendCode(sessionToken) {
  return post('/upload/resend-code', { sessionToken });
}

/**
 * Get upload form configuration (fields)
 * @param {string} sessionToken
 * @returns {Promise<{fields: Array}>}
 */
export async function getUploadConfig(sessionToken) {
  return get('/upload/config', {
    headers: {
      'X-Session-Token': sessionToken,
    },
  });
}

/**
 * Upload files
 * @param {string} sessionToken
 * @param {File[]} files
 * @param {Function} [onProgress] - Progress callback
 * @returns {Promise<{message: string, files: Array}>}
 */
export async function uploadFilesApi(sessionToken, files, onProgress = undefined) {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  
  return uploadFiles('/upload/files', formData, sessionToken);
}

/**
 * Submit the upload (complete the submission)
 * @param {string} sessionToken
 * @param {Object} formData - Form field values
 * @returns {Promise<{submissionId: string, message: string, downloadToken?: string}>}
 */
export async function submitUpload(sessionToken, formData) {
  const response = await fetch(`${API_BASE}/upload/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
    },
    credentials: 'include',
    body: JSON.stringify({ formData }),
  });
  return handleResponse(response);
}

// =============================================================================
// HEALTH API
// =============================================================================

/**
 * Check API health
 * @returns {Promise<{status: string, version: string, time: string}>}
 */
export async function checkHealth() {
  return get('/health');
}

// =============================================================================
// STORE - Reactive config store
// =============================================================================

import { writable } from 'svelte/store';

// Create a writable store for the public config
function createConfigStore() {
  const { subscribe, set, update } = writable({
    loaded: false,
    loading: false,
    error: null,
    organization: null,
    features: null,
    branding: null,
    form: null,
    notices: [],
  });

  return {
    subscribe,
    
    /**
     * Load the public config from API
     */
    async load() {
      update(state => ({ ...state, loading: true, error: null }));
      
      try {
        const data = await getPublicConfig();
        set({
          loaded: true,
          loading: false,
          error: null,
          organization: data.organization,
          features: data.features,
          branding: data.branding,
          form: data.form,
          notices: data.notices || [],
        });
        return data;
      } catch (error) {
        update(state => ({
          ...state,
          loading: false,
          error: error.message,
        }));
        throw error;
      }
    },
    
    /**
     * Reset the store
     */
    reset() {
      set({
        loaded: false,
        loading: false,
        error: null,
        organization: null,
        features: null,
        branding: null,
        form: null,
        notices: [],
      });
    },
  };
}

export const configStore = createConfigStore();

// Export default for convenience
export default {
  getPublicConfig,
  startUpload,
  verifyEmail,
  checkRememberToken,
  getRememberSettings,
  resendCode,
  getUploadConfig,
  uploadFiles: uploadFilesApi,
  submitUpload,
  checkHealth,
  configStore,
};
